import { z } from "zod";

import { DiscountType, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";
import { DiscountOrdinaryInputSchema } from "@/lib/zod/__generated__/validation-schema";
import { requiredValue } from "@/lib/zod/custom-validation-schema";

import {
	chillnnDateZodSchema,
	priceObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import * as DiscountItemShared from "../../../../_shared";

const ERROR_MESSAGES = {
	validDiscountAmount: {
		ratioMin: "割引率は0%以上を入力してください",
		ratioMax: "割引率は100%以下を入力してください",
	},
};

const DiscountAmountSchema = z.discriminatedUnion("discountType", [
	z.object({
		discountType: z.literal(DiscountType.Ratio),
		ratio: z
			.number()
			.int()
			.min(0, { message: ERROR_MESSAGES.validDiscountAmount.ratioMin })
			.max(100, { message: ERROR_MESSAGES.validDiscountAmount.ratioMax })
			.refine(requiredValue<number>, { message: "必須項目です" }),
	}),
	z.object({
		discountType: z.literal(DiscountType.Static),
		static: priceObjectZodSchema,
	}),
]);

/**
 * 通常ディスカウントの設定用のバリデーションスキーマ
 */
export const DiscountOrdinaryInputZodSchema = DiscountOrdinaryInputSchema.extend({
	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	stayableStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	stayableEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

})
	.merge(DiscountItemShared.Model.ValidationSchema.CommonDiscountInputZodSchema)
	// 公開する期間（設定する場合は開始日・終了日をセットで設定する）
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				return !!saleStart === !!saleEnd;
			}

			return true;
		},
		{
			message: "公開する期間は開始日と終了日の両方を設定してください",
			path: ["saleEnd"],
		},
	)
	// 公開する期間
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					return saleStart <= saleEnd;
				}

				return true;
			}

			return true;
		},
		{
			message: "終了日が開始日以降になるように設定してください",
			path: ["saleEnd"],
		},
	)
	// 滞在可能な期間
	.refine(
		({ stayableStart, stayableEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!stayableStart && !!stayableEnd) {
					return stayableStart <= stayableEnd;
				}

				// 片側だけ設定して保存するとサーバー側の適用判定で割引が一切適用されなくなるため、両方必須にする
				return false;
			}

			return true;
		},
		{
			message: "滞在可能な期間を設定してください",
			path: ["stayableEnd"],
		},
	)
	.and(DiscountAmountSchema);

export type DiscountOrdinaryInputZodSchemaType = z.infer<typeof DiscountOrdinaryInputZodSchema>;
